import { Month, FilterState, FilterType, CalendarEvent } from './common-types';

export const filterTypes: FilterType[] = Object.values(FilterType);

const parseEventDate = (dateString: string): Date => {
    const cleanDateString = dateString.replace(/A\.M|P\.M/gi, '').replace(/\./g, '');
    const period = (dateString.match(/(A\.M|P\.M)/gi) || ['A.M'])[0].toUpperCase();
    const date = new Date(cleanDateString);

    if (period === 'P.M' && date.getHours() < 12) {
        date.setHours(date.getHours() + 12);
    }
    if (period === 'A.M' && date.getHours() === 12) {
        date.setHours(0);
    }

    return date;
};

const makeEventComparator = (rank: (loc?: string) => number) =>
    (a: CalendarEvent, b: CalendarEvent) => {
        const dateA = parseEventDate(a.eventDate);
        const dateB = parseEventDate(b.eventDate);
        const byTime = dateA.getTime() - dateB.getTime();
        if (byTime !== 0) return byTime;

        const byRank = rank(a.location) - rank(b.location);
        if (byRank !== 0) return byRank;

        return (a.location || '').localeCompare(b.location || '');
    };

export const applyFilters = (events: Month[] | undefined, filterState: FilterState, orderOfVenueForEvents: string[]) => {
    if (!events) return;

    const rankMap = (orderOfVenueForEvents || [])
        .map((loc, idx) => [loc.toLowerCase(), idx] as const)
        .reduce<Record<string, number>>((acc, [loc, idx]) => { acc[loc] = idx; return acc; }, {});

    const rank = (loc?: string) => {
        if (!loc) return Number.MAX_SAFE_INTEGER;
        const r = rankMap[loc.toLowerCase()];
        return typeof r === 'number' ? r : Number.MAX_SAFE_INTEGER;
    };

    const compareEvents = makeEventComparator(rank);

    const filterKeys = Object.keys(filterState) as (keyof FilterState)[];
    const activeFilters = filterKeys.filter(filterType => {
        const applied = filterState[filterType];
        return applied?.value?.some(v => v !== "");
    });

    if (activeFilters.length === 0) {
        if (events.length !== 0) {
            return events.map(month => ({
                ...month,
                days: month.days.map(day => ({
                    ...day,
                    events: [...day.events].sort(compareEvents)
                }))
            }));
        } else {
            return events;
        }
    }

    const filterValueSets = new Map<keyof FilterState, Set<string>>(
        activeFilters.map(ft => [ft, new Set(filterState[ft].value)])
    );

    const filteredEvents = events.map(month => {
        let monthHasEvents = false;

        const filteredDays = month.days.map(day => {
            const sorted = [...day.events].sort(compareEvents);

            const matched = sorted.filter(event => {
                return activeFilters.some(activeFilter => {
                    const selected = filterValueSets.get(activeFilter)!;
                    let eventTags = (event as any)[activeFilter] as string | string[] | undefined;
                    if (!eventTags) return false;

                    const eventTagsArr = typeof eventTags === 'string' ? [eventTags] :
                        Array.isArray(eventTags) ? eventTags : [];

                    const hasMatch = eventTagsArr.some(tag => selected.has(tag));
                    if (hasMatch) monthHasEvents = true;
                    return hasMatch;
                });
            });

            return {
                ...day,
                events: matched
            };
        });

        return {
            ...month,
            anyEvents: monthHasEvents,
            days: filteredDays
        };
    });

    return filteredEvents;
}